import React, { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import ShopifySyncDialog from '@/components/products/ShopifySyncDialog';
import ProductEditor from '@/components/products/ProductEditor';
import { ewkBadgeLabels, ewkSuchtext } from '@/lib/ewk';
import { formatGewicht } from '@/lib/grundpreis';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Package, Search, Plus, Edit2, Archive, Download, Upload,
  Loader2, RefreshCw, AlertCircle, CheckCircle2, MoreHorizontal,
  FileSpreadsheet, Layers, X, Eye, Truck, MapPin, Recycle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseArrayField } from '@/lib/jsonField';
import { thumb, IMG_PROPS } from '@/lib/imageUrl';
import EmptyState from '@/components/ui/EmptyState';
import SkeletonCard from '@/components/ui/SkeletonCard';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import Papa from 'papaparse';

const product_b2b = (p) => {
  const v = p?.preis ?? p?.price;
  return v != null ? Number(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) + ' (Produkt)' : '—';
};

export default function AdminProducts() {
  const [searchTerm, setSearchTerm] = useState('');
  // Ein Editor für alles: Zeilenklick, Stift und „Neu" öffnen dieselbe Fläche.
  const [editorProduct, setEditorProduct] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [variantPreview, setVariantPreview] = useState(null);
  const queryClient = useQueryClient();

  // Products Query
  const { data: products = [], isLoading, refetch } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // PIM: Varianten aus product_variants (nicht mehr aus dem Legacy-JSON-Feld)
  const { data: allVariants = [] } = useQuery({
    queryKey: ['product-variants-all'],
    queryFn: () => base44.entities.ProductVariant.list('position'),
    refetchInterval: 30_000,
  });
  const variantsByProduct = React.useMemo(() => {
    const map = new Map();
    allVariants.forEach((v) => {
      if (v.aktiv === false) return;
      const list = map.get(v.product_id) || [];
      list.push(v);
      map.set(v.product_id, list);
    });
    return map;
  }, [allVariants]);

  // Görsel fallback: image_url boşsa product_images aynasındaki ilk görsel.
  const { data: allImages = [] } = useQuery({
    queryKey: ['product-images-all'],
    queryFn: () => base44.entities.ProductImage.list('position'),
    staleTime: 5 * 60_000,
  });
  const firstImageByProduct = React.useMemo(() => {
    const map = new Map();
    allImages.forEach((img) => {
      if (img?.url && !map.has(img.product_id)) map.set(img.product_id, img.url);
    });
    return map;
  }, [allImages]);
  const productImage = (p) => firstImageByProduct.get(p.id) || p.image_url || '';

  // EWK-Kategorien (Stammdaten) für Badges + Suche; Tabelle kann vor der
  // Migration fehlen → kein Retry, Liste bleibt leer.
  const { data: ewkKategorien = [] } = useQuery({
    queryKey: ['ewk-kategorien'],
    queryFn: () => base44.entities.EwkKategorie.list('position'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const ewkBadges = (p) => ewkBadgeLabels(p.ewk_kategorien, ewkKategorien);
  // Gewicht der Default- bzw. ersten aktiven Variante
  const productGewicht = (p) => {
    const vs = variantsByProduct.get(p.id) || [];
    const v = vs.find((x) => x.is_default && x.gewicht_kg != null) || vs.find((x) => x.gewicht_kg != null);
    return v?.gewicht_kg ?? null;
  };

  // Archivieren statt Löschen: products hat ON DELETE CASCADE auf
  // Kundenpreise, Lagerbewegungen, Varianten und Bilder. Ein echtes Löschen
  // vernichtet diese Historie unwiderruflich. Reaktivieren über den Editor.
  const archiveMutation = useMutation({
    mutationFn: (id) => base44.entities.Product.update(id, {
      is_active: false,
      aktiv: false,
      updated_date: new Date().toISOString(),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      toast.success('Produkt archiviert — Kundenpreise und Lagerhistorie bleiben erhalten');
    },
    onError: (err) => toast.error('Fehler: ' + err.message)
  });
  const archiveProduct = (product) => {
    if (window.confirm(`„${product.name}" archivieren? Es verschwindet aus Bestellungen und Katalog, alle Daten bleiben erhalten.`)) {
      archiveMutation.mutate(product.id);
    }
  };

  // "Ungezählt" filtresi: Eröffnungsinventur ile doğrulanmamış ürünleri
  // odaklanarak gösterir. Depo ekibi sayım ilerleyişini izlerken faydalı.
  const [showOnlyUncounted, setShowOnlyUncounted] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const isInactive = (p) => p.is_active === false || p.aktiv === false;
  const inactiveCount = products.filter(isInactive).length;

  const uncountedCount = products.filter(p => p.is_counted === false).length;

  const filteredProducts = products.filter(p => {
    if (!showInactive && isInactive(p)) return false;
    if (showOnlyUncounted && p.is_counted !== false) return false;
    const term = searchTerm.toLowerCase();
    return (
      p.name?.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term) ||
      (variantsByProduct.get(p.id) || []).some((v) => (v.sku || '').toLowerCase().includes(term)) ||
      p.category?.toLowerCase().includes(term) ||
      p.lagerort?.toLowerCase().includes(term) ||
      (term.length >= 3 && ewkSuchtext(p.ewk_kategorien, ewkKategorien).includes(term))
    );
  });

  const handleEdit = (product) => {
    setEditorProduct(product);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditorProduct(null);
    setEditorOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PageHeader
                title="Produkte"
                subtitle={<>Produktkatalog verwalten</>}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setImportModalOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Shopify Import
              </Button>
              <Button variant="outline" onClick={() => setSyncDialogOpen(true)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Katalog-Sync
              </Button>
              <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Neu
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle>Alle Produkte ({products.filter((p) => !isInactive(p)).length})</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant={showOnlyUncounted ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowOnlyUncounted((v) => !v)}
                  className={showOnlyUncounted ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  title="Eröffnungsinventur tamamlanmamış ürünleri göster"
                >
                  Ungezählt
                  {uncountedCount > 0 && (
                    <span className={`ml-2 rounded px-1.5 text-xs ${showOnlyUncounted ? 'bg-white/20' : 'bg-amber-100 text-amber-800'}`}>
                      {uncountedCount}
                    </span>
                  )}
                </Button>
                <Button
                  variant={showInactive ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowInactive((v) => !v)}
                  className={showInactive ? 'bg-slate-700 hover:bg-slate-800' : ''}
                  title="Deaktivierte / zusammengeführte Produkte einblenden"
                >
                  Inaktive
                  {inactiveCount > 0 && (
                    <span className={`ml-2 rounded px-1.5 text-xs ${showInactive ? 'bg-white/20' : 'bg-slate-100 text-slate-700'}`}>
                      {inactiveCount}
                    </span>
                  )}
                </Button>
                <div className="relative w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <SkeletonCard count={6} variant="row" />
            ) : filteredProducts.length === 0 ? (
              <EmptyState
                icon={Package}
                title="Keine Produkte"
                description="Es wurden keine Produkte gefunden. Erstellen Sie Ihr erstes Produkt oder importieren Sie eine CSV-Datei."
              />
            ) : (
              <>
                {/* Mobile: card list */}
                <div className="md:hidden space-y-3">
                  {filteredProducts.map((product) => {
                    const variants = variantsByProduct.get(product.id) || [];
                    const suppliers = parseArrayField(product.suppliers, { fieldName: 'suppliers' });
                    const stock = product.stock || 0;
                    const minStock = product.min_stock || 10;
                    const isLowStock = stock <= minStock && stock > 0;
                    const isOutOfStock = stock <= 0;
                    return (
                      <Card key={product.id} className="p-3 cursor-pointer" onClick={() => handleEdit(product)}>
                        <div className="flex items-start gap-3">
                          {productImage(product) ? (
                            <img {...IMG_PROPS} loading="lazy" width={64} height={64} src={thumb(productImage(product), 128)} alt={product.name} className="w-16 h-16 rounded-lg object-contain border bg-white flex-shrink-0" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center border border-dashed flex-shrink-0">
                              <Package className="w-6 h-6 text-slate-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-bold text-slate-900 truncate">{product.name}</p>
                                <p className="text-[10px] font-mono text-slate-500 uppercase tracking-wider truncate">{product.sku || 'KEINE SKU'}</p>
                              </div>
                              <Badge className={cn(
                                "flex-shrink-0 font-bold text-[9px] uppercase",
                                isOutOfStock ? "bg-red-100 text-red-700 border-red-200" :
                                isLowStock ? "bg-amber-100 text-amber-700 border-amber-200" :
                                "bg-emerald-100 text-emerald-700 border-emerald-200"
                              )} variant="outline">
                                {isOutOfStock ? 'Ausv.' : isLowStock ? 'Krit.' : 'OK'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <Badge variant="secondary" className="font-normal text-[10px]">{product.category || 'Allgemein'}</Badge>
                              <span className={cn(
                                "text-xs font-bold",
                                isOutOfStock ? "text-red-600" : isLowStock ? "text-amber-600" : "text-emerald-600"
                              )}>
                                {stock} Stk · Min {minStock}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 flex-wrap">
                              {variants.length > 1 && (
                                <span className="inline-flex items-center gap-0.5"><Layers className="w-2.5 h-2.5" />{variants.length}V · {variants.map((v) => v.sku).filter(Boolean).join(', ')}</span>
                              )}
                              {suppliers.length > 0 && (
                                <span className="inline-flex items-center gap-0.5"><Truck className="w-2.5 h-2.5" />{suppliers.length}L</span>
                              )}
                              {product.lagerort && (
                                <span className="inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{product.lagerort}</span>
                              )}
                              {productGewicht(product) != null && (
                                <span className="tabular-nums">{formatGewicht(productGewicht(product))}</span>
                              )}
                              {ewkBadges(product).length > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-emerald-700"><Recycle className="w-2.5 h-2.5" />EWK {ewkBadges(product).length}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100">
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(product); }} className="flex-1 min-h-[40px]">
                            <Edit2 className="w-4 h-4 mr-1" /> Bearbeiten
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); archiveProduct(product); }} aria-label="Archivieren" className="text-red-600 min-h-[40px] min-w-[44px]">
                            <Archive className="w-4 h-4" />
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Desktop: table (unchanged) */}
                <div className="hidden md:block rounded-md border overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-[80px]">Bild</TableHead>
                      <TableHead>Produkt / SKU</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead>Varianten</TableHead>
                      <TableHead>Lieferanten</TableHead>
                      <TableHead>EWK</TableHead>
                      <TableHead className="text-right">Gewicht</TableHead>
                      <TableHead>Lagerort</TableHead>
                      <TableHead className="text-center">Bestand (Ist)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((product) => {
                      const variants = variantsByProduct.get(product.id) || [];
                      const variantCount = variants.length;
                      const stock = product.stock || 0;
                      const minStock = product.min_stock || 10;
                      const isLowStock = stock <= minStock && stock > 0;
                      const isOutOfStock = stock <= 0;

                      let suppliers = product.suppliers || [];
                      if (typeof suppliers === 'string') {
                        try { suppliers = JSON.parse(suppliers); } catch { suppliers = []; }
                      }
                      const supplierCount = Array.isArray(suppliers) ? suppliers.length : 0;

                      return (
                        <TableRow key={product.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => handleEdit(product)}>
                          <TableCell>
                            {productImage(product) ? (
                              <img {...IMG_PROPS} loading="lazy" width={56} height={56} src={thumb(productImage(product), 112)} alt={product.name} className="w-14 h-14 rounded-lg object-contain border bg-white" />
                            ) : (
                              <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center border border-dashed">
                                <Package className="w-6 h-6 text-slate-300" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-900">{product.name}</span>
                              <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{product.sku || 'KEINE SKU'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="font-normal">{product.category || 'Allgemein'}</Badge>
                          </TableCell>
                          <TableCell>
                            {variantCount > 1 ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setVariantPreview({ ...product, _pim: variants }); }}
                                className="flex flex-col items-start gap-0.5 px-2 py-1 rounded bg-blue-50 text-[11px] text-blue-700 hover:bg-blue-100 transition-colors border border-blue-100 max-w-[220px]"
                              >
                                <span className="flex items-center gap-1 font-bold"><Layers className="w-3 h-3" />{variantCount} Varianten</span>
                                <span className="font-mono text-[10px] text-blue-600 truncate w-full text-left">
                                  {variants.map((v) => v.sku).filter(Boolean).join(' · ')}
                                </span>
                              </button>
                            ) : (
                              <span className="text-xs font-mono text-slate-500">{variants[0]?.sku || product.sku || '—'}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {supplierCount > 0 ? (
                              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 gap-1 font-normal text-[11px]">
                                <Truck className="w-3 h-3" />
                                {supplierCount} Lieferant{supplierCount !== 1 && 'en'}
                              </Badge>
                            ) : (
                              <span className="text-[11px] text-slate-400 italic">Keine</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {ewkBadges(product).length > 0 ? (
                              <div className="flex flex-wrap gap-1 max-w-[200px]">
                                {ewkBadges(product).map((b) => (
                                  <Badge key={b.schluessel} variant="outline" title={b.name}
                                    className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal text-[10px] px-1.5">
                                    {b.kurz}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-slate-700">
                            {formatGewicht(productGewicht(product))}
                          </TableCell>
                          <TableCell>
                            {product.lagerort ? (
                              <div className="flex items-center gap-1.5 text-slate-600">
                                <MapPin className="w-3 h-3" />
                                <span className="text-xs font-medium">{product.lagerort}</span>
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-400 italic">Nicht def.</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center">
                              <span className={cn(
                                "text-sm font-black",
                                isOutOfStock ? "text-red-600" : isLowStock ? "text-amber-600" : "text-emerald-600"
                              )}>
                                {stock}
                              </span>
                              <span className="text-[9px] text-slate-400">Min: {minStock}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={cn(
                              "font-bold text-[10px] uppercase",
                              isOutOfStock ? "bg-red-100 text-red-700 border-red-200" : 
                              isLowStock ? "bg-amber-100 text-amber-700 border-amber-200" : 
                              "bg-emerald-100 text-emerald-700 border-emerald-200"
                            )} variant="outline">
                              {isOutOfStock ? 'Ausverkauft' : isLowStock ? 'Kritisch' : 'Verfügbar'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(product); }} className="h-8 w-8 text-slate-500 hover:text-blue-600">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); archiveProduct(product); }}>
                                    <Archive className="w-4 h-4 mr-2" /> Archivieren
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <ProductEditor
        product={editorProduct}
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditorProduct(null); }}
      />

      <ShopifyImportModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        onSuccess={refetch}
      />

      <ShopifySyncDialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen} />

      {/* Variant Preview Dialog */}
      <Dialog open={!!variantPreview} onOpenChange={() => setVariantPreview(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              Varianten — {variantPreview?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(variantPreview?._pim || []).map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg border">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{v.title}</p>
                  <p className="text-xs font-mono text-slate-500">{v.sku || 'ohne SKU'}</p>
                </div>
                <div className="text-right text-xs tabular-nums shrink-0">
                  <p className="text-slate-500">Online {v.online_preis != null ? Number(v.online_preis).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '—'}</p>
                  <p className="text-slate-700 font-semibold">B2B {v.b2b_preis != null ? Number(v.b2b_preis).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : (product_b2b(variantPreview))}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Shopify CSV Import Modal
 * Parses Shopify product export CSV, groups rows by Title into products with variants.
 */
function ShopifyImportModal({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [parsedProducts, setParsedProducts] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setParsedProducts(null);
    setImportProgress(0);
    setImportResults(null);
    setIsAnalyzing(false);
    setIsImporting(false);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Bitte eine CSV-Datei auswählen');
      return;
    }

    setFile(selectedFile);
    setParsedProducts(null);
    setImportResults(null);

    // Parse immediately
    setIsAnalyzing(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const grouped = parseShopifyCSV(csvText);
        setParsedProducts(grouped);
        toast.success(`${grouped.length} Produkte erkannt`);
      } catch (err) {
        toast.error('CSV Fehler: ' + err.message);
        setFile(null);
      } finally {
        setIsAnalyzing(false);
      }
    };
    reader.readAsText(selectedFile);
  };

  /**
   * Parse Shopify CSV and group rows by Title.
   * Shopify exports one row per variant. Rows with empty Title belong to the previous product.
   */
  const parseShopifyCSV = (csvText) => {
    const result = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
    });

    if (result.errors.length > 0 && result.data.length === 0) {
      throw new Error('CSV konnte nicht gelesen werden');
    }

    const productsMap = new Map(); // title -> product data
    let currentTitle = '';

    for (const row of result.data) {
      const title = (row['Title'] || '').trim();

      // If title is present, start a new product group
      if (title) {
        currentTitle = title;
      }

      if (!currentTitle) continue;

      // Get or create product entry
      if (!productsMap.has(currentTitle)) {
        // Clean HTML from Body
        const bodyHtml = row['Body (HTML)'] || '';
        const cleanDesc = bodyHtml.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim();

        productsMap.set(currentTitle, {
          name: currentTitle,
          description: cleanDesc.substring(0, 2000),
          image_url: row['Image Src'] || '',
          variants: [],
          sku: '',
          price: 0,
          unit: 'Stück',
          stock_status: 'In Stock',
          is_active: true,
        });
      }

      const product = productsMap.get(currentTitle);

      // Add variant if Option1 Value exists
      const optionValue = (row['Option1 Value'] || '').trim();
      if (optionValue && optionValue !== 'Default Title') {
        const variantImage = (row['Variant Image'] || row['Image Src'] || '').trim();
        product.variants.push({
          value: optionValue,
          image: variantImage || product.image_url,
        });
      }

      // Use first available image as main image
      if (!product.image_url && row['Image Src']) {
        product.image_url = row['Image Src'];
      }
    }

    return Array.from(productsMap.values());
  };

  const handleImport = async () => {
    if (!parsedProducts || parsedProducts.length === 0) return;
    setIsImporting(true);
    setImportProgress(0);
    setStatusMessage('Bestehende Produkte werden abgeglichen...');

    const results = { success: 0, failed: 0, updated: 0, created: 0, errors: [] };

    // SKU-bazlı UPSERT: mevcut ürünler ASLA silinmez.
    // - SKU eşleşirse: yalnızca katalog metadatası güncellenir; stock,
    //   reserved_stock, lagerort, suppliers, min/melde/hoechst_bestand ve
    //   variants (stok taşıyıcı) DOKUNULMAZ. Stok hareket geçmişi ve
    //   müşteri fiyatları (FK) korunur.
    // - SKU eşleşmezse: yeni ürün oluşturulur.
    let existingBySku = new Map();
    try {
      const existingProducts = await base44.entities.Product.list();
      existingBySku = new Map(
        existingProducts
          .filter((p) => p.sku)
          .map((p) => [String(p.sku).trim().toLowerCase(), p])
      );
    } catch (e) {
      console.warn('Could not load existing products for upsert:', e);
    }

    setStatusMessage('Produkte werden importiert (Upsert per SKU)...');
    const productsToImport = [...parsedProducts];

    for (let i = 0; i < productsToImport.length; i++) {
      const product = productsToImport[i];
      const skuKey = String(product.sku || '').trim().toLowerCase();
      const existing = skuKey ? existingBySku.get(skuKey) : null;

      try {
        if (existing) {
          // GÜNCELLE: yalnızca katalog alanları — stok/depo alanları korunur
          await base44.entities.Product.update(existing.id, {
            name: product.name,
            description: product.description || existing.description || '',
            image_url: product.image_url || existing.image_url || '',
            unit: product.unit || existing.unit || 'Stück',
            is_active: true,
          });
          results.updated++;
        } else {
          const productData = {
            name: product.name,
            description: product.description || '',
            image_url: product.image_url || '',
            variants: product.variants || [],
            sku: product.sku || '',
            price: 0,
            unit: product.unit || 'Stück',
            stock_status: 'In Stock',
            is_active: true,
          };
          try {
            await base44.entities.Product.create(productData);
          } catch {
            // variants kolonu yoksa varyantsız dene
            const { variants: _v, ...fallbackData } = productData;
            await base44.entities.Product.create(fallbackData);
            console.warn('Imported without variants:', product.name);
          }
          results.created++;
        }
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push({ product: product.name, error: error.message });
      }
      setImportProgress(Math.round(((i + 1) / productsToImport.length) * 100));
    }

    setStatusMessage('');
    setImportResults(results);
    setIsImporting(false);

    if (results.success > 0) {
      toast.success(`Import: ${results.created} neu, ${results.updated} aktualisiert (Bestände unverändert)`);
      onSuccess();
    }
    if (results.failed > 0) {
      toast.error(`${results.failed} Produkte fehlgeschlagen`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Shopify CSV Import
          </DialogTitle>
          <DialogDescription>
            Laden Sie Ihre Shopify Produkt-Export CSV-Datei hoch. Produkte mit Varianten werden automatisch gruppiert.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* CSV Format Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-semibold text-blue-800 mb-1.5">📋 Erwartetes Shopify CSV Format:</p>
            <div className="flex flex-wrap gap-1">
              {['Title', 'Body (HTML)', 'Option1 Name', 'Option1 Value', 'Image Src', 'Variant Image'].map(col => (
                <span key={col} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-mono">
                  {col}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-blue-600 mt-1.5">
              Zeilen mit gleichem <strong>Title</strong> werden als Varianten zusammengeführt.
            </p>
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
              ${file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            {file ? (
              <>
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                <p className="font-medium text-emerald-700 text-sm">{file.name}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {isAnalyzing ? 'Wird analysiert...' : parsedProducts ? `${parsedProducts.length} Produkte erkannt` : ''}
                </p>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                <p className="font-medium text-slate-700 text-sm">CSV-Datei auswählen</p>
                <p className="text-xs text-slate-400 mt-1">Shopify Product Export (.csv)</p>
              </>
            )}
          </div>

          {/* Loading */}
          {isAnalyzing && (
            <div className="flex items-center justify-center py-2 gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
              <span className="text-sm text-slate-600">CSV wird analysiert...</span>
            </div>
          )}

          {/* Preview */}
          {parsedProducts && !importResults && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700">Vorschau</h4>
                <Badge variant="outline" className="text-xs">
                  {parsedProducts.length} Produkte • {parsedProducts.reduce((s, p) => s + (p.variants?.length || 0), 0)} Varianten
                </Badge>
              </div>

              <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                {parsedProducts.slice(0, 15).map((product, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                    {product.image_url ? (
                      <img {...IMG_PROPS} loading="lazy" width={36} height={36} src={thumb(product.image_url, 72)} alt="" className="w-9 h-9 rounded object-cover border flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <Package className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{product.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {product.variants.length > 0 && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                            {product.variants.length} Var.
                          </span>
                        )}
                        {product.description && (
                          <span className="text-[10px] text-slate-400 truncate max-w-[200px]">
                            {product.description.substring(0, 60)}...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {parsedProducts.length > 15 && (
                  <p className="text-xs text-slate-400 text-center py-1">
                    ... und {parsedProducts.length - 15} weitere Produkte
                  </p>
                )}
              </div>

              {/* Warning: replaces all products */}
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700 font-medium">⚠️ Alle bestehenden Produkte werden gelöscht und durch die CSV-Daten ersetzt.</p>
              </div>

              <Button
                onClick={handleImport}
                disabled={isImporting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importiere...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    {parsedProducts.length} Produkte importieren
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Import Progress */}
          {isImporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{statusMessage || 'Import läuft...'}</span>
                <span className="font-medium">{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}

          {/* Import Results */}
          {importResults && (
            <div className="space-y-3">
              <div className={`p-4 rounded-lg text-center ${importResults.failed > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                {importResults.failed === 0 ? (
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                ) : (
                  <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
                )}
                <p className="font-semibold text-sm">Import abgeschlossen</p>
                <div className="flex justify-center gap-4 text-sm mt-2">
                  <span className="text-emerald-600 font-medium">
                    ✓ {importResults.success} erfolgreich
                  </span>
                  {importResults.failed > 0 && (
                    <span className="text-red-600 font-medium">
                      ✗ {importResults.failed} fehlgeschlagen
                    </span>
                  )}
                </div>
              </div>

              <Button onClick={handleClose} className="w-full" variant="outline">
                Schließen
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
